// src/hooks/useFCMDeviceToken.js
import { useEffect } from "react";
import { getToken, onMessage, isSupported } from "firebase/messaging";
import { messaging } from "../firebase";
import { api } from "../api/api";

// La bacheca clinic (Fetta B «BOARD - CHAT»): il valore di `tipo` e
// l'indirizzo sono il calco di `QPSC_TIPO_BACHECA` / `qpscUrlBacheca` in
// `public/firebase-messaging-sw.js`, che non passa dal bundler e non può
// importarli. Chi cambia l'uno cambi l'altro (e `avvisaBachecaClinic` nel
// backend, che li manda).
const TIPO_BACHECA = "clinic-board";

// Fetta C: con `groupId` (il coach dell'evento) la vista del coach del gruppo.
function urlDellaBacheca(eventId, personId, groupId) {
  if (!eventId) return personId ? "/clinics" : "/clinic-events";
  const evento = encodeURIComponent(eventId);
  if (!personId) return `/clinic-events/${evento}`;
  const base = `/clinics/board/${evento}/${encodeURIComponent(personId)}`;
  return groupId ? `${base}?group=${encodeURIComponent(groupId)}` : base;
}

export default function useFCMDeviceToken(user) {
  useEffect(() => {
    const ident = user?.firebaseUid;
    if (!ident) return;

    let unsub = () => {};
    (async () => {
      if (!(await isSupported())) return;

      // Il gestore dei messaggi in primo piano si registra SUBITO: non
      // dipende dal token né dal permesso. Stava in fondo, dopo due `return`
      // che escono dall'INTERA funzione e non dal solo blocco del token, e
      // così non veniva registrato per chi avesse già il token salvato —
      // cioè a ogni riapertura dell'app (P11-push, forma C1; il dettaglio
      // sta nei rapporti del punto).
      unsub = onMessage(messaging, (payload) => {
        // ⚠️ TITOLO E CORPO VENGONO DAI DATI. Il backend manda ora un messaggio
        // SOLO-DATI (Fetta C), così ad app chiusa la libreria Firebase non
        // mostra più una notifica propria in aggiunta alla nostra: il blocco
        // `notification` non arriva più, e leggerlo darebbe una notifica vuota.
        // I nomi `titolo`/`corpo` sono lo stesso patto scritto in
        // `POST /messages` e letto dal service worker.
        const title = payload?.data?.titolo || "QPSC";
        const body  = payload?.data?.corpo  || "";
        const icon  = "/images/logo-192.png";
        // ⚠️ SI APRE LA CHAT, NON LA PERSONA. `chatId` è l'id della
        // CONVERSAZIONE, ed è quello che vuole l'indirizzo di /messaging
        // (`?chat=`). Qui si usava `senderId` — l'id di una PERSONA, il
        // soggetto mostrato: in un gruppo di squadra o in un annuncio i due
        // valori non coincidono MAI, e si finiva sulla conversazione privata
        // con quella persona invece che nel gruppo. Da questa fetta il backend
        // non manda più `senderId` del tutto: non aveva più un lettore.
        const chatId = payload?.data?.chatId;

        // LA BACHECA CLINIC (Fetta B «BOARD - CHAT», 26/9/2026): il patto a
        // tre si ESTENDE. Con `tipo === "clinic-board"` la destinazione è la
        // bacheca per persona (`personId`) o, senza, l'evento della plancia
        // (l'amministratore) — la stessa regola del service worker
        // (`qpscUrlBacheca`). Ogni altra push resta della chat, come prima.
        const eBacheca = payload?.data?.tipo === TIPO_BACHECA;
        const gruppoBacheca = eBacheca ? payload?.data?.groupId : null;
        const urlBacheca = eBacheca
          ? urlDellaBacheca(payload?.data?.eventId, payload?.data?.personId, gruppoBacheca)
          : null;

        // Mostra la notifica di sistema solo se:
        // - permesso granted
        // - pagina non visibile
        // - la chat bersaglio NON è già quella aperta
        //   (per la bacheca: la bacheca per persona NON è già quella aperta)
        const chatOpen = eBacheca
          ? Boolean(payload?.data?.personId) &&
            (gruppoBacheca
              ? window.location.pathname + window.location.search
              : window.location.pathname) === urlBacheca
          : window.location.pathname === "/messaging" &&
            new URLSearchParams(window.location.search).get("chat") === chatId;

        // ⚠️ AD APP VISIBILE NON SI SQUILLA (decisione B1). `onMessage` scatta
        // quando la pagina è in primo piano: sei DENTRO l'app, e una notifica
        // di sistema sarebbe intrusiva — l'arrivo si riflette in-app (App.jsx,
        // ascoltatore di `qpsc-push`; e su /messaging il pallino dei non-letti
        // si aggiorna già da solo, essendo letto da un `onSnapshot`).
        // Il ramo qui sotto resta come RETE per il caso in cui il gestore di
        // primo piano venga chiamato a pagina NON visibile: lì la notifica ci
        // vuole, ed è lo stesso metro del service worker (silenzio solo se sei
        // già su quella chat).
        if (Notification.permission === "granted" &&
            document.visibilityState !== "visible" &&
            !chatOpen) {
          const n = new Notification(title, { body, icon, data: { chatId } });
          n.onclick = () => {
            try { window.focus(); } catch {}
            // Percorso RELATIVO: vale in beta come in produzione.
            if (eBacheca) {
              window.location.href = urlBacheca;
            } else if (chatId) {
              window.location.href = `/messaging?chat=${encodeURIComponent(chatId)}`;
            }
            n.close();
          };
        }

        // L'AVVISO IN-APP. L'evento lo ascolta App.jsx (punto globale, montato
        // accanto a questo hook): fino al Blocco 5 non lo ascoltava nessuno e
        // l'arrivo spariva in silenzio.
        try {
          window.dispatchEvent(new CustomEvent("qpsc-push", {
            detail: { title, body, chatId, payload }
          }));
        } catch {}
      });

      // 1) PERMESSO NOTIFICHE — e viene PRIMA della registrazione.
      // L'ordine è cambiato di proposito (Blocco 5, Fetta B, lavoro D): il
      // service worker si registra SOLO per chi ha davvero concesso il
      // permesso. Prima si registrava comunque, e a chi aveva detto di no
      // restava installato un worker che non avrebbe mai potuto mostrare
      // nulla. A permesso già concesso o già negato la chiamata non mostra
      // alcun popup e risponde subito.
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return;

      // 2) SW REGISTRATO ALLA ROOT — ⚠️ SEDE UNICA della registrazione.
      // `index.js` non registra più nulla: era il secondo punto vivo, nudo e
      // incondizionato. Se serve registrare il worker delle notifiche, si fa
      // qui e in nessun altro posto. La registrazione deve precedere
      // `getToken`, che la riceve esplicitamente (punto 3) e non ne crea una
      // propria.
      const registration =
        (await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js")) ||
        (await navigator.serviceWorker.register("/firebase-messaging-sw.js"));

      // 3) Token legato al SW registrato
      try {
        const currentToken = await getToken(messaging, {
          // Chiave VAPID dell'ambiente in uso (frontend/.env.*): è pubblica,
          // ma specifica del progetto Firebase. Vedi CLAUDE.md §2.
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: registration
        });

        if (!user || user.status !== "ACTIVE") return;

        const savedToken = localStorage.getItem("qpsc_fcm_token");
        if (savedToken === currentToken) return;

        if (currentToken) {
          // ⚠️ SOLO IL TOKEN. Il `userId` non si manda più: la rotta
          // `/push/register` ha ora `requireAuth` e ricava l'identità DAL
          // TOKEN d'accesso (Fetta A), risolvendola al documento anagrafico.
          // Il campo nel corpo era per giunta sbagliato — portava il
          // `firebaseUid`, che il backend usava come id-documento — ed è
          // esattamente la strada per cui chiunque poteva iscrivere il proprio
          // dispositivo sulla scheda di un altro.
          await api.post("/push/register", { token: currentToken });
        }

        localStorage.setItem("qpsc_fcm_token", currentToken);

      } catch (e) {
        console.warn("getToken/save error:", e);
      }

    })();

    return () => { try { unsub(); } catch {} };
  }, [user]);
}